import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Building2,
  Loader2,
  ArrowLeft,
  UserPlus,
  CheckCircle,
  CreditCard,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

export default function OrganizationFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    is_active: true,
    // Admin creation fields
    create_admin: false,
    admin_email: '',
    admin_name: '',
    admin_password: ''
  });

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard');
      return;
    }
    if (isEdit) {
      fetchOrganization();
      fetchStripeStatus();
    }
  }, [isSuperAdmin, isEdit, id, navigate]);

  const fetchOrganization = async () => {
    try {
      const response = await organizationsAPI.getOne(id);
      setFormData({
        name: response.data.name,
        contact_email: response.data.contact_email || '',
        contact_phone: response.data.contact_phone || '',
        address: response.data.address || '',
        is_active: response.data.is_active
      });
    } catch (error) {
      toast.error('Failed to load organization');
      navigate('/admin/organizations');
    } finally {
      setFetching(false);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await organizationsAPI.getStripeConnectStatus(id);
      setStripeStatus(response.data);
    } catch {
      setStripeStatus(null);
    }
  };

  const handleStripeConnect = async () => {
    setStripeLoading(true);
    try {
      const response = await organizationsAPI.initiateStripeConnect(id, window.location.origin);
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      } else if (response.data.status === 'active') {
        toast.success('Organization is already connected to Stripe');
        fetchStripeStatus();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to initiate Stripe Connect';
      toast.error(msg);
    } finally {
      setStripeLoading(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const response = await organizationsAPI.getStripeDashboard(id);
      if (response.data.dashboard_url) {
        window.open(response.data.dashboard_url, '_blank');
      }
    } catch {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    // Validate admin fields if creating admin
    if (!isEdit && formData.create_admin) {
      if (!formData.admin_email || !formData.admin_name || !formData.admin_password) {
        toast.error('Please fill all admin fields or disable admin creation');
        return;
      }
      if (formData.admin_password.length < 6) {
        toast.error('Admin password must be at least 6 characters');
        return;
      }
    }

    setLoading(true);
    try {
      if (isEdit) {
        await organizationsAPI.update(id, {
          name: formData.name,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          address: formData.address,
          is_active: formData.is_active
        });
        toast.success('Organization updated successfully');
      } else {
        const createData = {
          name: formData.name,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          address: formData.address
        };
        
        // Include admin credentials if creating admin
        if (formData.create_admin) {
          createData.admin_email = formData.admin_email;
          createData.admin_name = formData.admin_name;
          createData.admin_password = formData.admin_password;
        }
        
        const response = await organizationsAPI.create(createData);
        
        if (response.data.admin_created) {
          toast.success(
            <div>
              <p className="font-semibold">Organization created successfully!</p>
              <p className="text-sm">Org Admin account created for {response.data.admin_email}</p>
            </div>
          );
        } else {
          toast.success('Organization created successfully');
        }
      }
      navigate('/admin/organizations');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} organization`);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      {/* Main Content */}
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/admin/organizations" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground font-body text-sm mb-4 transition-colors"
            data-testid="back-to-orgs"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Organizations
          </Link>
          <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
            {isEdit ? 'Edit Organization' : 'Add New Organization'}
          </h1>
          <p className="font-body text-muted-foreground">
            {isEdit ? 'Update organization details' : 'Create a new client company portal'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-w-xl" data-testid="org-form">
          <div className="bg-card rounded-xl border border-border p-8 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-body">Organization Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter organization name"
                className="font-body bg-card"
                required
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_email" className="font-body">Contact Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                placeholder="contact@company.com"
                className="font-body bg-card"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_phone" className="font-body">Contact Phone</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="font-body bg-card"
                data-testid="phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="font-body">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Enter full address"
                className="font-body bg-card"
                rows={3}
                data-testid="address-input"
              />
            </div>

            {isEdit && (
              <div className="flex items-center justify-between py-4 border-t border-b border-border">
                <div>
                  <Label className="font-body font-medium">Organization Active</Label>
                  <p className="font-body text-sm text-muted-foreground">
                    Disabled organizations cannot access their portal
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange('is_active', checked)}
                  data-testid="is-active-switch"
                />
              </div>
            )}

            {/* Admin Creation Section - Only for new organizations */}
            {!isEdit && (
              <div className="border-t border-border pt-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <UserPlus className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <Label className="font-body font-medium text-base">Create Org Admin</Label>
                      <p className="font-body text-sm text-muted-foreground">
                        Automatically create the first admin user for this organization
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.create_admin}
                    onCheckedChange={(checked) => handleChange('create_admin', checked)}
                    data-testid="create-admin-switch"
                  />
                </div>

                {formData.create_admin && (
                  <div className="space-y-4 p-4 bg-secondary/50 rounded-lg animate-fadeIn">
                    <div className="flex items-center gap-2 text-sm text-primary mb-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Admin user will be created with org_admin role</span>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="admin_name" className="font-body">Admin Name *</Label>
                      <Input
                        id="admin_name"
                        value={formData.admin_name}
                        onChange={(e) => handleChange('admin_name', e.target.value)}
                        placeholder="John Smith"
                        className="font-body bg-card"
                        data-testid="admin-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="admin_email" className="font-body">Admin Email *</Label>
                      <Input
                        id="admin_email"
                        type="email"
                        value={formData.admin_email}
                        onChange={(e) => handleChange('admin_email', e.target.value)}
                        placeholder="admin@company.com"
                        className="font-body bg-card"
                        data-testid="admin-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="admin_password" className="font-body">Admin Password *</Label>
                      <Input
                        id="admin_password"
                        type="password"
                        value={formData.admin_password}
                        onChange={(e) => handleChange('admin_password', e.target.value)}
                        placeholder="Minimum 6 characters"
                        className="font-body bg-card"
                        data-testid="admin-password-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Share these credentials with the organization administrator
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stripe Connect Section - Only for existing organizations */}
            {isEdit && (
              <div className="border-t border-border pt-6 mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <CreditCard className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-body font-medium text-base">Stripe Connect</h3>
                    <p className="font-body text-sm text-muted-foreground">
                      Connect this organization's Stripe account to receive payments directly (5% platform fee)
                    </p>
                  </div>
                </div>

                {stripeStatus?.status === 'active' ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-body font-medium text-green-800">Stripe Connected</span>
                    </div>
                    <p className="font-body text-sm text-green-700">
                      Payments from client invoices will be sent directly to this organization's Stripe account.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleStripeDashboard}
                      data-testid="stripe-dashboard-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Stripe Dashboard
                    </Button>
                  </div>
                ) : stripeStatus?.status === 'pending_verification' || stripeStatus?.status === 'onboarding_incomplete' ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <span className="font-body font-medium text-amber-800">
                        {stripeStatus.status === 'pending_verification' ? 'Pending Verification' : 'Onboarding Incomplete'}
                      </span>
                    </div>
                    <p className="font-body text-sm text-amber-700">
                      {stripeStatus.status === 'pending_verification'
                        ? 'Stripe is verifying this account. This usually takes 1-2 business days.'
                        : 'The organization needs to complete the Stripe onboarding process.'}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleStripeConnect}
                      disabled={stripeLoading}
                      data-testid="stripe-resume-btn"
                    >
                      {stripeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                      Resume Onboarding
                    </Button>
                  </div>
                ) : stripeStatus?.status === 'stripe_not_configured' ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-slate-500" />
                      <span className="font-body font-medium text-slate-700">Stripe Not Configured</span>
                    </div>
                    <p className="font-body text-sm text-slate-600">
                      A real Stripe API key with Connect enabled is required. Update the platform's Stripe configuration to enable this feature.
                    </p>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleStripeConnect}
                    disabled={stripeLoading}
                    className="w-full justify-center py-6 border-dashed"
                    data-testid="stripe-connect-btn"
                  >
                    {stripeLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connecting...</>
                    ) : (
                      <><CreditCard className="w-5 h-5 mr-2" />Connect Stripe Account</>
                    )}
                  </Button>
                )}
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-4 pt-4">
              <Link to="/admin/organizations">
                <Button variant="outline" className="font-body" data-testid="cancel-btn">
                  Cancel
                </Button>
              </Link>
              <Button 
                type="submit" 
                className="btn-lift bg-primary text-white font-body"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEdit ? 'Update Organization' : 'Create Organization'
                )}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
